import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Settings, Plus, Search, Pencil, Trash2, Globe, Monitor, AlertCircle, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const CATEGORY_STYLES = {
  productive: { dot: 'bg-emerald-500', text: 'text-emerald-600 dark:text-emerald-400', bg: 'bg-emerald-50 dark:bg-emerald-900/20', label: 'Productive' },
  unproductive: { dot: 'bg-rose-500', text: 'text-rose-600 dark:text-rose-400', bg: 'bg-rose-50 dark:bg-rose-900/20', label: 'Unproductive' },
  neutral: { dot: 'bg-slate-400', text: 'text-slate-600 dark:text-slate-400', bg: 'bg-slate-100 dark:bg-slate-800', label: 'Neutral' }
};

export default function UsageCategoryAdmin() {
  const { user } = useAuth();
  const [categories, setCategories] = useState([]);
  const [uncategorized, setUncategorized] = useState([]);
  const [settings, setSettings] = useState(null);
  const [search, setSearch] = useState('');
  const [filterCategory, setFilterCategory] = useState('all');
  const [loading, setLoading] = useState(true);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [newRule, setNewRule] = useState({ name: '', type: 'domain', category: 'productive' });
  const [saving, setSaving] = useState(false);

  const token = localStorage.getItem('inspirotracker_token');
  const headers = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };

  const fetchCategories = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      if (filterCategory !== 'all') params.append('category', filterCategory);

      const res = await fetch(`${API}/api/usage/categories?${params}`, { headers: { 'Authorization': `Bearer ${token}` } });
      const data = await res.json();
      setCategories(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Failed to fetch categories:', err);
    }
  }, [search, filterCategory, token]);

  const fetchUncategorized = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/usage/uncategorized`, { headers: { 'Authorization': `Bearer ${token}` } });
      const data = await res.json();
      setUncategorized(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Failed to fetch uncategorized:', err);
    }
  }, [token]);

  const fetchSettings = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/usage/settings`, { headers: { 'Authorization': `Bearer ${token}` } });
      const data = await res.json();
      setSettings(data);
    } catch (err) {
      console.error('Failed to fetch settings:', err);
    }
  }, [token]);

  useEffect(() => {
    Promise.all([fetchCategories(), fetchUncategorized(), fetchSettings()]).finally(() => setLoading(false));
  }, [fetchCategories, fetchUncategorized, fetchSettings]);

  const handleAddRule = async () => {
    if (!newRule.name.trim()) return toast.error('Name is required');
    setSaving(true);
    try {
      const res = await fetch(`${API}/api/usage/categories`, {
        method: 'POST',
        headers,
        body: JSON.stringify(newRule)
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail);
      toast.success('Category rule added');
      setShowAddDialog(false);
      setNewRule({ name: '', type: 'domain', category: 'productive' });
      fetchCategories();
      fetchUncategorized();
    } catch (err) {
      toast.error(err.message || 'Failed to add rule');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateCategory = async (id, category) => {
    try {
      const res = await fetch(`${API}/api/usage/categories/${id}`, {
        method: 'PUT',
        headers,
        body: JSON.stringify({ category })
      });
      if (!res.ok) throw new Error((await res.json()).detail);
      toast.success('Category updated');
      fetchCategories();
      setEditItem(null);
    } catch (err) {
      toast.error(err.message || 'Failed to update');
    }
  };

  const handleDelete = async (id) => {
    try {
      const res = await fetch(`${API}/api/usage/categories/${id}`, { method: 'DELETE', headers });
      if (!res.ok) throw new Error((await res.json()).detail);
      toast.success('Rule deleted');
      fetchCategories();
    } catch (err) {
      toast.error(err.message || 'Failed to delete');
    }
  };

  const handleQuickCategorize = async (name, type, category) => {
    setSaving(true);
    try {
      const res = await fetch(`${API}/api/usage/categories`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ name, type, category })
      });
      if (!res.ok) throw new Error((await res.json()).detail);
      toast.success(`"${name}" categorized as ${category}`);
      fetchCategories();
      fetchUncategorized();
    } catch (err) {
      toast.error(err.message || 'Failed to categorize');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateSettings = async (updates) => {
    try {
      const res = await fetch(`${API}/api/usage/settings`, {
        method: 'PUT',
        headers,
        body: JSON.stringify(updates)
      });
      if (!res.ok) throw new Error((await res.json()).detail);
      const data = await res.json();
      setSettings(data);
      toast.success('Settings updated');
    } catch (err) {
      toast.error(err.message || 'Failed to update settings');
    }
  };

  if (user?.role !== 'admin') {
    return (
      <Layout>
        <div className="flex items-center justify-center h-full p-8">
          <p className="text-slate-500 dark:text-slate-400">Admin access required.</p>
        </div>
      </Layout>
    );
  }

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center py-16">
          <div className="animate-spin w-8 h-8 border-4 border-indigo-500 border-t-transparent rounded-full" />
        </div>
      </Layout>
    );
  }

  const filteredCategories = categories;

  return (
    <Layout>
    <div className="space-y-6 p-1" data-testid="usage-category-admin">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-100" data-testid="category-admin-title">
            <Settings className="w-6 h-6 inline-block mr-2 mb-1" />
            App & Website Categories
          </h1>
          <p className="text-sm text-slate-500 dark:text-slate-400">Manage how apps and websites are categorized for productivity tracking</p>
        </div>
        <Button onClick={() => setShowAddDialog(true)} className="bg-indigo-600 hover:bg-indigo-700 text-white" data-testid="add-rule-btn">
          <Plus className="w-4 h-4 mr-1" /> Add Rule
        </Button>
      </div>

      <Tabs defaultValue="categories" className="w-full">
        <TabsList className="h-auto">
          <TabsTrigger value="categories" data-testid="tab-categories">Categories</TabsTrigger>
          <TabsTrigger value="uncategorized" data-testid="tab-uncategorized">
            Uncategorized {uncategorized.length > 0 && <span className="ml-1 px-1.5 py-0.5 text-xs bg-amber-100 text-amber-700 rounded-full">{uncategorized.length}</span>}
          </TabsTrigger>
          <TabsTrigger value="settings" data-testid="tab-settings">Settings</TabsTrigger>
        </TabsList>

        {/* Categories Tab */}
        <TabsContent value="categories" className="mt-4">
          <Card className="border border-slate-200 dark:border-slate-700">
            <CardContent className="p-4">
              {/* Filters */}
              <div className="flex flex-col sm:flex-row gap-3 mb-4">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    placeholder="Search apps or domains..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="pl-9"
                    data-testid="category-search"
                  />
                </div>
                <Select value={filterCategory} onValueChange={setFilterCategory}>
                  <SelectTrigger className="w-[160px]" data-testid="category-filter-select">
                    <SelectValue placeholder="All categories" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All categories</SelectItem>
                    <SelectItem value="productive">Productive</SelectItem>
                    <SelectItem value="unproductive">Unproductive</SelectItem>
                    <SelectItem value="neutral">Neutral</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Table */}
              <div className="rounded-lg border border-slate-200 dark:border-slate-700 overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-50 dark:bg-slate-800/50 border-slate-200 dark:border-slate-700">
                      <TableHead className="text-xs font-semibold uppercase tracking-wider w-16">Type</TableHead>
                      <TableHead className="text-xs font-semibold uppercase tracking-wider">Name</TableHead>
                      <TableHead className="text-xs font-semibold uppercase tracking-wider">Category</TableHead>
                      <TableHead className="text-xs font-semibold uppercase tracking-wider w-20">Source</TableHead>
                      <TableHead className="text-xs font-semibold uppercase tracking-wider w-20 text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredCategories.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={5} className="text-center py-8 text-slate-400">No categories found</TableCell>
                      </TableRow>
                    ) : (
                      filteredCategories.map((rule) => {
                        const style = CATEGORY_STYLES[rule.category];
                        return (
                          <TableRow key={rule.id} className="border-slate-200 dark:border-slate-700" data-testid={`category-row-${rule.id}`}>
                            <TableCell>
                              {rule.type === 'domain' ? (
                                <Globe className="w-4 h-4 text-blue-500" />
                              ) : (
                                <Monitor className="w-4 h-4 text-purple-500" />
                              )}
                            </TableCell>
                            <TableCell className="font-medium text-slate-900 dark:text-slate-100">{rule.name}</TableCell>
                            <TableCell>
                              {editItem === rule.id ? (
                                <Select
                                  value={rule.category}
                                  onValueChange={(val) => handleUpdateCategory(rule.id, val)}
                                >
                                  <SelectTrigger className="w-[140px] h-8">
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="productive">Productive</SelectItem>
                                    <SelectItem value="unproductive">Unproductive</SelectItem>
                                    <SelectItem value="neutral">Neutral</SelectItem>
                                  </SelectContent>
                                </Select>
                              ) : (
                                <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium ${style.bg} ${style.text}`}>
                                  <span className={`w-1.5 h-1.5 rounded-full ${style.dot}`} />
                                  {style.label}
                                </span>
                              )}
                            </TableCell>
                            <TableCell>
                              <span className={`text-xs ${rule.is_default ? 'text-slate-400' : 'text-indigo-500 font-medium'}`}>
                                {rule.is_default ? 'Default' : 'Custom'}
                              </span>
                            </TableCell>
                            <TableCell className="text-right">
                              <div className="flex items-center justify-end gap-1">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => setEditItem(editItem === rule.id ? null : rule.id)}
                                  className="h-7 w-7 p-0"
                                  data-testid={`edit-category-${rule.id}`}
                                >
                                  <Pencil className="w-3.5 h-3.5" />
                                </Button>
                                {!rule.is_default && (
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => handleDelete(rule.id)}
                                    className="h-7 w-7 p-0 text-rose-500 hover:text-rose-600"
                                    data-testid={`delete-category-${rule.id}`}
                                  >
                                    <Trash2 className="w-3.5 h-3.5" />
                                  </Button>
                                )}
                              </div>
                            </TableCell>
                          </TableRow>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
              </div>
              <p className="text-xs text-slate-400 mt-2">{filteredCategories.length} rules</p>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Uncategorized Tab */}
        <TabsContent value="uncategorized" className="mt-4">
          <Card className="border border-slate-200 dark:border-slate-700">
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <AlertCircle className="w-4 h-4 text-amber-500" />
                Uncategorized Apps & Websites
              </CardTitle>
              <p className="text-sm text-slate-500 dark:text-slate-400">These were detected in usage data but don't have a category assigned yet</p>
            </CardHeader>
            <CardContent>
              {uncategorized.length === 0 ? (
                <div className="text-center py-8 text-slate-400">All detected apps and websites are categorized</div>
              ) : (
                <div className="space-y-3">
                  {uncategorized.map((item, idx) => (
                    <div
                      key={idx}
                      className="flex items-center justify-between p-3 rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/30"
                      data-testid={`uncategorized-item-${idx}`}
                    >
                      <div className="flex items-center gap-3">
                        {item.type === 'domain' ? (
                          <Globe className="w-4 h-4 text-blue-500" />
                        ) : (
                          <Monitor className="w-4 h-4 text-purple-500" />
                        )}
                        <div>
                          <p className="font-medium text-slate-900 dark:text-slate-100">{item.name}</p>
                          <p className="text-xs text-slate-400">Seen {item.occurrences} times</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-7 text-xs border-emerald-300 text-emerald-600 hover:bg-emerald-50"
                          onClick={() => handleQuickCategorize(item.name, item.type, 'productive')}
                          disabled={saving}
                          data-testid={`categorize-productive-${idx}`}
                        >
                          Productive
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-7 text-xs border-rose-300 text-rose-600 hover:bg-rose-50"
                          onClick={() => handleQuickCategorize(item.name, item.type, 'unproductive')}
                          disabled={saving}
                          data-testid={`categorize-unproductive-${idx}`}
                        >
                          Unproductive
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-7 text-xs border-slate-300 text-slate-600 hover:bg-slate-50"
                          onClick={() => handleQuickCategorize(item.name, item.type, 'neutral')}
                          disabled={saving}
                          data-testid={`categorize-neutral-${idx}`}
                        >
                          Neutral
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Settings Tab */}
        <TabsContent value="settings" className="mt-4">
          <Card className="border border-slate-200 dark:border-slate-700">
            <CardHeader>
              <CardTitle className="text-base">Tracking Settings</CardTitle>
              <p className="text-sm text-slate-500 dark:text-slate-400">Configure how usage tracking works for your organization</p>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="flex items-center justify-between">
                <div>
                  <Label className="text-sm font-medium">Enable Usage Tracking</Label>
                  <p className="text-xs text-slate-400 mt-0.5">When disabled, agents will stop collecting data</p>
                </div>
                <Switch
                  checked={settings?.tracking_enabled ?? true}
                  onCheckedChange={(val) => handleUpdateSettings({ tracking_enabled: val })}
                  data-testid="tracking-enabled-switch"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm font-medium">Working Hours Start</Label>
                  <Input
                    type="time"
                    value={settings?.working_hours_start || '09:00'}
                    onChange={(e) => handleUpdateSettings({ working_hours_start: e.target.value })}
                    className="mt-1"
                    data-testid="working-hours-start"
                  />
                </div>
                <div>
                  <Label className="text-sm font-medium">Working Hours End</Label>
                  <Input
                    type="time"
                    value={settings?.working_hours_end || '18:00'}
                    onChange={(e) => handleUpdateSettings({ working_hours_end: e.target.value })}
                    className="mt-1"
                    data-testid="working-hours-end"
                  />
                </div>
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <Label className="text-sm font-medium">Track Weekends</Label>
                  <p className="text-xs text-slate-400 mt-0.5">Enable tracking on Saturday and Sunday</p>
                </div>
                <Switch
                  checked={settings?.track_weekends ?? false}
                  onCheckedChange={(val) => handleUpdateSettings({ track_weekends: val })}
                  data-testid="track-weekends-switch"
                />
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Add Rule Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Category Rule</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Name</Label>
              <Input
                placeholder="e.g., figma.com or Notion"
                value={newRule.name}
                onChange={(e) => setNewRule({ ...newRule, name: e.target.value })}
                className="mt-1"
                data-testid="new-rule-name"
              />
            </div>
            <div>
              <Label>Type</Label>
              <Select value={newRule.type} onValueChange={(val) => setNewRule({ ...newRule, type: val })}>
                <SelectTrigger className="mt-1" data-testid="new-rule-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="domain">Website (Domain)</SelectItem>
                  <SelectItem value="app">Application</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Category</Label>
              <Select value={newRule.category} onValueChange={(val) => setNewRule({ ...newRule, category: val })}>
                <SelectTrigger className="mt-1" data-testid="new-rule-category">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="productive">Productive</SelectItem>
                  <SelectItem value="unproductive">Unproductive</SelectItem>
                  <SelectItem value="neutral">Neutral</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddDialog(false)} data-testid="cancel-add-rule">Cancel</Button>
            <Button onClick={handleAddRule} disabled={saving} className="bg-indigo-600 hover:bg-indigo-700 text-white" data-testid="save-add-rule">
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null}
              Add Rule
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
    </Layout>
  );
}
